import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './styles.css'
import App from './App.jsx'
import CrashScreen from './components/CrashScreen'

// Initialize Sentry for error monitoring only (no replay, tracing, or metrics)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.PROD ? 'production' : 'development',
    release: __APP_VERSION__,
    // Error monitoring only — disable tracing, replay, and metrics explicitly
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  })
}

// Planning Center OAuth popup relay.
//
// pco-oauth-callback finishes with a plain browser redirect to
// /admin/process?pco=<status>. When that lands inside the connect popup, the
// popup is a dead end — the parent dashboard is the window that needs to know.
// So before React boots we check for an opener and hand the status back, then
// close. Runs at module scope precisely so it never costs a React mount: the
// popup renders nothing and is gone in a frame.
//
// If there is no opener the redirect is a normal full-page return and this
// does nothing, which is what keeps the non-popup path (popup blocker, an
// in-app browser that refuses window.open) working untouched.
;(function relayPcoPopup() {
  try {
    const status = new URLSearchParams(window.location.search).get('pco')
    if (!status || !window.opener || window.opener === window) return
    // Targeted at our own origin only — a wildcard would leak the status to
    // whatever page happened to open us.
    window.opener.postMessage({ source: 'beseter-pco', status }, window.location.origin)
    window.close()
  } catch {
    // Cross-origin opener, or a browser that refuses close(). Fall through and
    // render normally — the page still shows the status notice itself.
  }
})()

const root = createRoot(document.getElementById('root'))
root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={CrashScreen}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>
)

// Production only. Vite's dev server already does its own module-level
// caching and hot reload; a service worker sitting in front of that fights
// HMR rather than helping anything, so it never runs for `npm run dev`.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
  })
}
